import { useState, useEffect } from "react";
import * as XLSX from 'xlsx';
import Layout from "../../components/layout/Layout";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { toast } from 'react-toastify';
import {
  ActionContainer,
  AddUser,
  DataGridContainer,
  DataGridWrapper,
} from "./style";
import { ordersColumns } from "./ordersForm";
import { getOrderInvoice, getOrderDeliveryNote, getOrders, deleteOrder } from "../../api/ordersService";
import EventSelect from "../../components/custom/EventSelect";
import { useAuth } from "../../hooks/useAuth";

type ListType = "User" | "Order" | "Order";

const OrdersPage = () => {


  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const type: ListType = "Order"
  // const data = useData(type);
  const [data, setData] = useState<any[]>([]);
  const [allOrders, setAllOrders] = useState<any[]>([]);
  const [eventId, setEventId] = useState('');
  const [showOldEvents, setShowOldEvents] = useState(() => {
    // Restore checkbox state from localStorage
    const stored = localStorage.getItem('orders_show_old_events');
    return stored === 'true';
  });

  // Helper function to check if event is upcoming (end_date >= today)
  const isEventUpcoming = (order: any): boolean => {
    if (!order.event?.end_date) {
      return false; // If no end_date, exclude it when filtering
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
    const eventEndDate = new Date(order.event.end_date);
    eventEndDate.setHours(0, 0, 0, 0);
    return eventEndDate >= today;
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
      return;
    }
    
    try {
      await deleteOrder(id);
      // Refresh the orders list
      if (eventId) {
        getOrdersByEvent(eventId);
      } else {
        const res = await getOrders();
        if (res) {
          setAllOrders(res);
          // Apply filter based on showOldEvents state
          if (showOldEvents) {
            setData(res);
          } else {
            const filtered = res.filter((order: any) => isEventUpcoming(order));
            setData(filtered);
          }
        }
      }
    } catch (err: any) {
      console.error('Error deleting order:', err);
      alert('Failed to delete order. Please try again.');
    }
  };

  const downloadInvoice = async (order_id: string) => {
    const fetchInvoicePdf = async () => {
      try {
        let response = await getOrderInvoice(order_id);
        // Create a URL for the fetched PDF
        const url = URL.createObjectURL(response);
        
        const now = new Date().toISOString().replace(/[-T:.Z]/g, "");
        const link = document.createElement('a');
        link.href = url;
        link.download = `Invoice_${now}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

      } catch (error: any) {
        console.error('Error fetching the invoice PDF:', error);
        
        let errorMessage = 'Error fetching the invoice PDF';
        
        // Handle error response from server (404 with JSON message)
        if (error.response) {
          try {
            // If responseType is 'blob', the error data is also a blob
            if (error.response.data instanceof Blob) {
              const text = await error.response.data.text();
              const errorData = JSON.parse(text);
              errorMessage = errorData.message || errorMessage;
            } else if (typeof error.response.data === 'object' && error.response.data.message) {
              errorMessage = error.response.data.message;
            } else if (error.response.status === 404) {
              errorMessage = 'Invoice not found. Please checkout the payment in advance';
            }
          } catch (parseError) {
            // If parsing fails, use default message
            errorMessage = error.response.status === 404 
              ? 'Invoice not found. Please checkout the payment in advance'
              : errorMessage;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
      }
    };

    // if (event_id && booth_no) {
    // }
    fetchInvoicePdf();
  };

  const downloadDeliveryNote = async (order_id: string) => {
    const fetchDeliveryPdf = async () => {
      try {
        const response = await getOrderDeliveryNote(order_id);
        const url = URL.createObjectURL(response);
        const now = new Date().toISOString().replace(/[-T:.Z]/g, "");
        const link = document.createElement('a');
        link.href = url;
        link.download = `Delivery_${now}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error: any) {
        console.error('Error fetching the delivery note PDF:', error);
        let errorMessage = 'Error fetching the delivery note PDF';
        if (error.response) {
          try {
            if (error.response.data instanceof Blob) {
              const text = await error.response.data.text();
              const errorData = JSON.parse(text);
              errorMessage = errorData.message || errorMessage;
            } else if (typeof error.response.data === 'object' && error.response.data.message) {
              errorMessage = error.response.data.message;
            } else if (error.response.status === 404) {
              errorMessage = 'Delivery note not found.';
            }
          } catch (parseError) {
            errorMessage = error.response.status === 404 ? 'Delivery note not found.' : errorMessage;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
      }
    };
    fetchDeliveryPdf();
  };

  const actions: GridColDef[] = [
    {
      field: "action",
      headerName: "Action",
      width: 320,
      renderCell: (params) => {
        return (
          <ActionContainer>
            <Link
              to={`/orders/${params.row.id}`}
              state={{ ...params.row }}
              style={{ textDecoration: "none" }}
            >
              <button className="btn viewBtn">View</button>
            </Link>
             <button
              onClick={() => downloadInvoice(params.row.id)}
              className="btn viewBtn"
            >
              Invoice
            </button>
            <button
              onClick={() => downloadDeliveryNote(params.row.id)}
              className="btn viewBtn"
            >
              Delivery
            </button>
            {isAdmin && (
            <button
              onClick={() => handleDelete(params.row.id)}
              className="btn deleteBtn"
            >
              Delete
            </button>
            )}
          </ActionContainer>
        );
      },
    },
  ];
  const getOrdersByEvent = (event_id: any) => {
    if (event_id) {
      localStorage.setItem('orders_event_id', event_id);
      setEventId(event_id);
      getOrders(`event_id=${event_id}`).then(results => {
        if (results) {
          setAllOrders(results);
          // Apply filter based on showOldEvents state
          if (showOldEvents) {
            setData(results); // Show all orders
          } else {
            const filtered = results.filter((order: any) => isEventUpcoming(order));
            setData(filtered); // Show only upcoming events
          }
        }
      }).catch(err => {
        console.log(err.message);
      })
    } else {
      // Clear event filter
      clearEventFilter();
    }
  }

  const clearEventFilter = () => {
    localStorage.removeItem('orders_event_id');
    setEventId('');
    getOrders().then(results => {
      if (results) {
        setAllOrders(results);
        // Apply filter based on showOldEvents state
        if (showOldEvents) {
          setData(results); // Show all orders
        } else {
          const filtered = results.filter((order: any) => isEventUpcoming(order));
          setData(filtered); // Show only upcoming events
        }
      }
    }).catch(err => {
      console.log(err.message);
    })
  }
  const exportAsExcel = async () => {
    // Use allOrders for export (includes all orders regardless of filter)
    const ordersToExport = allOrders.length > 0 ? allOrders : data;
    
    // Define the columns for the Excel sheet
    const headers = [
      "Event",
      "Exhibitor",
      "Booth Number",
      "Contact Name",
      "Contact Email",
      "Contact Number",
      "Order Date",
      "Status",
      "Payment",
      "Product Code",
      "Item",
      "Category",
      "Quantity",
      "Item Price",
      "Total Price (net)",
      "VAT",
      "Total Price (Gross)"
    ];

    // Prepare data for each order and its products
    const excelData = [headers];

    ordersToExport.forEach(order => {
      order.order_items?.forEach((item: any) => {
        const netPrice = parseFloat(item.price) * item.quantity || 0;
        const productTax =
          order.event?.product_tax != null && order.event?.product_tax !== ''
            ? parseFloat(order.event.product_tax)
            : parseFloat(order.event?.tax || 0);
        const taxRate = (Number.isNaN(productTax) ? 0 : productTax) / 100;
        const vat = netPrice * taxRate;
        const grossPrice = netPrice * (1 + taxRate);
        
        excelData.push([
          order.event?.full_name || "", // Event name
          order.company?.name || "", // Exhibitor name company
          order.booth_no || "", // Booth Number 
          order.user?.username || "", // Contact Name
          order.user?.email || "", // Contact Email
          order.company?.phone || "", // Contact Number
          order.created_at ? new Date(order.created_at).toISOString().slice(0, 10) : "", // Order Date (YYYY-MM-DD)
          order.status || "pending", // Status (pending, approved)
          order.payment_status || "unpaid", // Payment (pending, succeeded)
          item.product?.code || "", // Product Code
          item.product?.name || "", // Item name
          item.product?.category?.name || "", // Category
          item.quantity || 0, // Quantity
          parseFloat(item.price) || 0, // Item Price
          parseFloat(netPrice.toFixed(2)), // Total Price (net)
          parseFloat(vat.toFixed(2)), // VAT (20% of net) - fixed to 2 decimals
          parseFloat(grossPrice.toFixed(2)) // Total Price (Gross) - fixed to 2 decimals
        ]);
      });
    });

    // Create a worksheet from the data
    const worksheet = XLSX.utils.aoa_to_sheet(excelData);

    // Create a workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "ProductSheet");

    // Generate the Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Create a Blob for the Excel file
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

    // Create a link to download the file with a dynamic name
    const dateTime = new Date().toISOString().replace(/[:.-]/g, '_').split('T').join('_');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Exhibitor_Product_Report_${dateTime}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  useEffect(() => {
    const fetchData = async () => {
      // Check localStorage for previously selected event
      const storedEventId = localStorage.getItem('orders_event_id');
      
      if (storedEventId) {
        // Restore the event filter
        setEventId(storedEventId);
        // Fetch orders for the stored event
        const res = await getOrders(`event_id=${storedEventId}`);
        if (res) {
          setAllOrders(res);
          // Apply filter based on showOldEvents state
          if (showOldEvents) {
            setData(res); // Show all orders
          } else {
            const filtered = res.filter((order: any) => isEventUpcoming(order));
            setData(filtered); // Show only upcoming events
          }
        }
      } else {
        // No stored event, fetch all orders
        const res = await getOrders();
        if (res) {
          setAllOrders(res);
          // Apply filter based on showOldEvents state
          if (showOldEvents) {
            setData(res); // Show all orders
          } else {
            const filtered = res.filter((order: any) => isEventUpcoming(order));
            setData(filtered); // Show only upcoming events
          }
        }
      }
    }
    fetchData();
  }, [])

  // Re-filter when showOldEvents changes
  useEffect(() => {
    if (allOrders.length > 0) {
      if (showOldEvents) {
        setData(allOrders); // Show all orders
      } else {
        const filtered = allOrders.filter((order: any) => isEventUpcoming(order));
        setData(filtered); // Show only upcoming events
      }
    }
  }, [showOldEvents, allOrders])
  return (
    <Layout>
      <AddUser>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              padding: '5px',
              color: '#5949d1'
            }}
            title="Go back"
          >
            <ArrowBackIcon />
          </button>
          <h1 className="title">Orders</h1>
        </div>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
          <EventSelect onSelect={getOrdersByEvent} selected={eventId} />
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showOldEvents}
              onChange={(e) => {
                const newValue = e.target.checked;
                setShowOldEvents(newValue);
                // Save to localStorage
                localStorage.setItem('orders_show_old_events', newValue.toString());
              }}
              style={{ cursor: 'pointer' }}
            />
            <span>Show old events orders</span>
          </label>
        </div>
        {/* <Link
          to={`/orders/new`}
          state={{ type }}
          style={{ textDecoration: "none" }}
        >
          <button className="add">Add {type}</button>
        </Link> */}
        <button
          onClick={exportAsExcel}
          className="btn exportBtn"
        >
          Export
        </button>
      </AddUser>
      <DataGridWrapper>
        <DataGridContainer>
          <DataGrid
            style={{ backgroundColor: "white" }}
            rows={data}
            columns={
              ordersColumns.concat(actions)
            }
            pageSize={10}
            rowsPerPageOptions={[10]}
          // checkboxSelection
          />
        </DataGridContainer>
      </DataGridWrapper>
    </Layout >
  );
};

export default OrdersPage;
